"use client";

import { useCallback, useEffect, useState } from "react";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { analyticsSetup, currentPage, type AnalyticsEvent } from "./analytics";
import { createTracker, type Tracker } from "./tracker";

/**
 * The project's analytics tracker, if it has one.
 *
 * Set up once per project: the SDKs install global state — a script tag, a
 * `dataLayer`, a queue — so initialising twice would double every hit. The
 * project never changes while the app is open, so once is enough.
 *
 * The tracker is React state rather than a ref, which matters because its SDK is
 * fetched on demand. The returned callbacks change identity when it arrives, so
 * an effect that reports something on mount — the first page view, before the
 * visitor has done anything — runs again once there is somewhere to report it to,
 * instead of firing into the gap and being lost.
 *
 * Nothing here logs. Legacy narrated each call to the console, one line per page
 * view, which is noise in every visitor's console and tells anyone who opens it
 * what the project's analytics setup is.
 */
export function useAnalytics() {
  const configs = useProjectConfigs();
  const [tracker, setTracker] = useState<Tracker | null>(null);

  const setup = analyticsSetup(configs);
  const type = setup?.type;
  const id = setup?.id;
  const matomoBaseUrl = setup?.matomoBaseUrl;

  useEffect(() => {
    if (!type || !id) return;

    let cancelled = false;
    const start = () => {
      void createTracker({ type, id, matomoBaseUrl }).then((loaded) => {
        if (!cancelled) setTracker(loaded);
      });
    };

    // Analytics has no first-paint value, so let map construction and hydration
    // finish before the tracker chunk competes for the main thread and network.
    const hasIdle = typeof window.requestIdleCallback === "function";
    const handle = hasIdle
      ? window.requestIdleCallback(start, { timeout: 3000 })
      : window.setTimeout(start, 2000);

    return () => {
      cancelled = true;
      if (hasIdle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
      setTracker(null);
    };
  }, [type, id, matomoBaseUrl]);

  const pageView = useCallback(() => {
    tracker?.pageView(currentPage(window.location));
  }, [tracker]);

  const event = useCallback(
    (event: AnalyticsEvent) => tracker?.event(event),
    [tracker]
  );

  const setDimensions = useCallback(
    (dimensions: Record<string, string>) => tracker?.setDimensions(dimensions),
    [tracker]
  );

  return { pageView, event, setDimensions, isEnabled: !!setup };
}
